/**
 * React Query hook for polling async chat research job status.
 *
 * This hook implements automatic polling for long-running research operations
 * that would otherwise timeout. It polls every 2 seconds while the job is
 * processing and automatically stops when complete or failed.
 */

'use client';

import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '@/lib/api-client-hooks';
import { getRuntimeConfig } from '@/lib/runtime-config';
import { normalizeApiBaseUrl } from '@/lib/utils';

const API_BASE_URL = normalizeApiBaseUrl(getRuntimeConfig().apiBaseUrl || process.env.REACT_APP_API_BASE_URL || '');

export interface JobProgress {
  stage: string;
  percent: number;
}

export interface ChatJobStatus {
  jobId: string;
  status: 'pending' | 'processing' | 'complete' | 'failed' | 'not_found';
  progress?: JobProgress;
  result?: any;
  error?: string;
}

/**
 * Hook to poll async research job status.
 *
 * Polls the REST endpoint every 2 seconds while the job is active
 * (pending or processing) and stops when the job completes or fails.
 *
 * @param jobId - Celery task ID to poll (null to disable polling)
 * @param enabled - Whether polling is enabled (default: true)
 * @returns React Query result with job status data
 *
 * @example
 * ```tsx
 * const { data: jobStatus, isLoading } = useChatJobPolling(jobId);
 *
 * if (jobStatus?.status === 'complete') {
 *   // Handle completion
 * }
 * ```
 */
export function useChatJobPolling(jobId: string | null, enabled: boolean = true) {
  const { fetchJSON } = useAuthenticatedFetch();

  return useQuery<ChatJobStatus>({
    queryKey: ['chatJobStatus', jobId],
    queryFn: async () => {
      console.log('[useChatJobPolling] Fetching status for job:', jobId);
      const result = await fetchJSON<ChatJobStatus>(`${API_BASE_URL}/api/backend/chat/jobs/${jobId}/status`);
      console.log('[useChatJobPolling] Received status:', result);
      return result;
    },
    enabled: enabled && jobId !== null,
    // Poll every 2 seconds while processing
    refetchInterval: query => {
      // Access data from query state
      const data = query.state.data;
      if (data?.status === 'processing' || data?.status === 'pending') {
        return 2000; // 2 seconds
      }
      // Stop polling when complete/failed
      return false;
    },
    // Continue polling even when tab is not focused
    refetchIntervalInBackground: true,
    // Always fetch fresh status (no stale time)
    staleTime: 0,
    // Keep in cache for 1 minute after component unmount
    gcTime: 60000,
    // Retry once on network error
    retry: 1,
    retryDelay: 1000,
  });
}
